import { isBlockingRequiredElement } from "../modal/consentSelection";
import { noticePurposeRows, productPurposeKey } from "../core/productMatrix";
import type { ProductLike } from "../types";
import type { Purpose } from "../api/types";
import type {
  InlineElementFlags,
  InlineNoticePurpose,
  InlinePurposeRow,
  InlineSelectablePurpose,
  InlineSelection,
} from "./types";

export { isBlockingRequiredElement };

export const hasBlockingRequiredElement = (purpose: {
  data_elements: Array<{ enabled: boolean; required: boolean }>;
}): boolean => purpose.data_elements.some(isBlockingRequiredElement);

export const inlineElementKey = (
  productUuid: string | undefined,
  purposeUuid: string,
  elementUuid: string
): string => `${productPurposeKey(productUuid, purposeUuid)}-${elementUuid}`;

export const inlinePurposeRows = <P extends ProductLike, U extends InlineSelectablePurpose>(config: {
  products?: P[];
  purposes: U[];
  product_purpose_order?: Record<string, string[]>;
}): InlinePurposeRow<U>[] =>
  noticePurposeRows(config.products, config.purposes, config.product_purpose_order);

export const initialCollapsedPurposes = <U extends InlineSelectablePurpose>(
  rows: InlinePurposeRow<U>[]
): Record<string, boolean> =>
  rows.reduce<Record<string, boolean>>((acc, row) => {
    acc[productPurposeKey(row.productUuid, row.purpose.uuid)] = true;
    return acc;
  }, {});

export const initialSelection = <U extends InlineSelectablePurpose>(
  rows: InlinePurposeRow<U>[]
): InlineSelection => {
  const selectedPurposes: Record<string, boolean> = {};
  const selectedDataElements: Record<string, boolean> = {};
  rows.forEach(({ purpose, productUuid }) => {
    selectedPurposes[productPurposeKey(productUuid, purpose.uuid)] = false;
    purpose.data_elements.forEach((element) => {
      selectedDataElements[inlineElementKey(productUuid, purpose.uuid, element.uuid)] = false;
    });
  });
  return { selectedPurposes, selectedDataElements };
};

export const allRequiredElementsChecked = <U extends InlineSelectablePurpose>(
  rows: InlinePurposeRow<U>[],
  selectedDataElements: Record<string, boolean>
): boolean =>
  rows.every(({ purpose, productUuid }) =>
    purpose.data_elements
      .filter(isBlockingRequiredElement)
      .every((element) =>
        Boolean(selectedDataElements[inlineElementKey(productUuid, purpose.uuid, element.uuid)])
      )
  );

export const withPurposeElements = (
  purpose: InlineSelectablePurpose,
  productUuid: string | undefined,
  selected: boolean,
  selectedDataElements: Record<string, boolean>
): Record<string, boolean> => {
  const updated = { ...selectedDataElements };
  purpose.data_elements.forEach((element) => {
    updated[inlineElementKey(productUuid, purpose.uuid, element.uuid)] =
      selected && element.enabled;
  });
  return updated;
};

export const toggleDataElement = (
  purpose: InlineSelectablePurpose,
  elementUuid: string,
  productUuid: string | undefined,
  selectedDataElements: Record<string, boolean>
): { selectedDataElements: Record<string, boolean>; purposeSelected: boolean } => {
  const combinedId = inlineElementKey(productUuid, purpose.uuid, elementUuid);
  const next = { ...selectedDataElements, [combinedId]: !selectedDataElements[combinedId] };
  const isTicked = (element: InlineElementFlags) =>
    Boolean(next[inlineElementKey(productUuid, purpose.uuid, element.uuid)]);
  const requiredElements = purpose.data_elements.filter(isBlockingRequiredElement);
  const purposeSelected =
    requiredElements.length > 0
      ? requiredElements.every(isTicked)
      : purpose.data_elements.some(isTicked);
  return { selectedDataElements: next, purposeSelected };
};

export const buildInlineSubmitPurposes = (
  rows: InlinePurposeRow<InlineNoticePurpose>[],
  { selectedPurposes, selectedDataElements }: InlineSelection
): Purpose[] =>
  rows.map(({ purpose, productUuid }) => {
    const key = productPurposeKey(productUuid, purpose.uuid);
    const isTicked = (elementUuid: string) =>
      selectedDataElements[`${key}-${elementUuid}`] || false;
    const hasSelectedRequiredElement = purpose.data_elements.some(
      (element) => element.required && isTicked(element.uuid)
    );
    const hasSelectedDataElement = purpose.data_elements.some((element) =>
      isTicked(element.uuid)
    );
    const purposeSelected =
      selectedPurposes[key] || hasSelectedRequiredElement || hasSelectedDataElement;
    return {
      ...purpose,
      ...(productUuid ? { product_uuid: productUuid } : {}),
      selected: purposeSelected,
      data_elements: purpose.data_elements.map((element) => ({
        ...element,
        selected: isTicked(element.uuid),
      })),
    };
  });

export const inlineSubmissionKey = (
  accessToken: string | undefined,
  configUuid: string | undefined,
  { selectedPurposes, selectedDataElements }: InlineSelection
): string =>
  JSON.stringify([accessToken ?? null, configUuid ?? null, selectedPurposes, selectedDataElements]);

export const isRetryableFailure = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status !== "number" || status >= 500;
};
